import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { motion } from 'framer-motion';

const ExportInquiryForm = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState(false);
  
  const { register, handleSubmit, formState: { errors }, reset } = useForm();

  const onSubmit = async (data) => {
    setIsSubmitting(true);
    setSubmitError(false);
    
    try {
      // Create FormData object for FormSubmit.co
      const formData = new FormData();
      
      // Add all form fields to FormData
      formData.append('firstName', data.firstName);
      formData.append('lastName', data.lastName);
      formData.append('email', data.email);
      formData.append('company', data.company);
      formData.append('phone', data.phone || '');
      formData.append('productInterest', data.productInterest);
      formData.append('message', data.message);
      
      // Add additional metadata
      formData.append('_subject', `New Export Inquiry from ${data.firstName} ${data.lastName}`);
      formData.append('_template', 'table');
      formData.append('_captcha', 'false');
      
      // Add CC email to send to both addresses
      formData.append('_cc', 'exquisitecoralafrica@gmail.com');
      
      // Submit to FormSubmit.co
      const response = await fetch('https://formsubmit.co/amirtalib24@gmail.com', {
        method: 'POST',
        body: formData
      });

      if (response.ok) {
        setSubmitSuccess(true);
        reset();
        // Reset success message after 5 seconds
        setTimeout(() => setSubmitSuccess(false), 5000);
      } else {
        throw new Error('Form submission failed');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmitError(true);
      // Reset error message after 5 seconds
      setTimeout(() => setSubmitError(false), 5000);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="py-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="max-w-2xl mx-auto bg-white rounded-xl shadow-lg p-8"
        >
          {submitSuccess ? (
            <div className="text-center py-8">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100">
                <svg className="h-6 w-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h3 className="mt-4 text-lg font-medium text-gray-900">Thank you for your inquiry!</h3>
              <p className="mt-2 text-sm text-gray-500">
                We'll get back to you shortly with more information about our premium agricultural exports.
              </p>
              <button
                onClick={() => setSubmitSuccess(false)}
                className="mt-4 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700"
              >
                Submit Another Inquiry
              </button>
            </div>
          ) : submitError ? (
            <div className="text-center py-8">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
                <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </div>
              <h3 className="mt-4 text-lg font-medium text-gray-900">Submission Failed</h3>
              <p className="mt-2 text-sm text-gray-500">
                There was an error submitting your inquiry. Please try again or contact us directly.
              </p>
              <div className="mt-4 space-y-2">
                <button
                  onClick={() => setSubmitError(false)}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 mr-3"
                >
                  Try Again
                </button>
                <a
                  href="mailto:exquisitecoralafrica@gmail.com"
                  className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                >
                  Email Us Directly
                </a>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                <div>
                  <label htmlFor="firstName" className="block text-sm font-medium text-gray-700">
                    First Name
                  </label>
                  <input
                    type="text"
                    id="firstName"
                    {...register('firstName', { required: 'First name is required' })}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm"
                  />
                  {errors.firstName && (
                    <p className="mt-1 text-sm text-red-600">{errors.firstName.message}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="lastName" className="block text-sm font-medium text-gray-700">
                    Last Name
                  </label>
                  <input
                    type="text"
                    id="lastName"
                    {...register('lastName', { required: 'Last name is required' })}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm"
                  />
                  {errors.lastName && (
                    <p className="mt-1 text-sm text-red-600">{errors.lastName.message}</p>
                  )}
                </div>
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                  Email
                </label>
                <input
                  type="email"
                  id="email"
                  {...register('email', {
                    required: 'Email is required',
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                      message: 'Invalid email address',
                    },
                  })}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm"
                />
                {errors.email && (
                  <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>
                )}
              </div>

              <div>
                <label htmlFor="company" className="block text-sm font-medium text-gray-700">
                  Company Name
                </label>
                <input
                  type="text"
                  id="company"
                  {...register('company', { required: 'Company name is required' })}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm"
                />
                {errors.company && (
                  <p className="mt-1 text-sm text-red-600">{errors.company.message}</p>
                )}
              </div>

              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-gray-700">
                  Phone Number
                </label>
                <input
                  type="tel"
                  id="phone"
                  {...register('phone')}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm"
                  placeholder="Optional"
                />
              </div>

              <div>
                <label htmlFor="productInterest" className="block text-sm font-medium text-gray-700">
                  Products of Interest
                </label>
                <select
                  id="productInterest"
                  {...register('productInterest', { required: 'Please select a product category' })}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm"
                >
                  <option value="">Select a category</option>
                  
                  {/* Horticultural Products */}
                  <optgroup label="Horticultural Products">
                    <option value="Fruits">Fresh Fruits (Avocados, Mangoes, Pineapples, etc.)</option>
                    <option value="Vegetables">Fresh Vegetables (Tomatoes, Onions, Okra, etc.)</option>
                    <option value="Medicinal Herbs">Medicinal Herbs (Moringa, Neem, Aloe Vera, etc.)</option>
                  </optgroup>
                  
                  {/* Agricultural Commodities */}
                  <optgroup label="Agricultural Commodities">
                    <option value="Cocoa Products">Cocoa Products (Beans, Butter, Cake)</option>
                    <option value="Tubers & Derivatives">Tubers & Derivatives (Cassava, Yams, Gari, etc.)</option>
                    <option value="Edible Nuts">Edible Nuts (Cashews, Groundnuts, Walnuts)</option>
                    <option value="Natural Fibers">Natural Fibers (Cotton, Jute, Coconut Fibers)</option>
                    <option value="Spices">Spices (Ginger, Pepper, Chilies, Cloves)</option>
                    <option value="Gum Arabic">Gum Arabic (All Grades)</option>
                    <option value="Crude Drugs">Crude Drugs (Kola Nuts, Bitter Kola)</option>
                  </optgroup>
                  
                  {/* Oil Seeds & Derivatives */}
                  <optgroup label="Oil Seeds & Derivatives">
                    <option value="Raw Oil Seeds">Raw Oil Seeds (Shea Nuts, Sesame, Coconut)</option>
                    <option value="Processed Oils">Processed Oils (Shea Butter, Palm Oil, Groundnut Oil)</option>
                    <option value="Oil Cakes">Oil Cakes & By-products</option>
                  </optgroup>
                  
                  {/* Processed Products */}
                  <optgroup label="Processed/Manufactured Products">
                    <option value="Processed Foods">Processed Food Products</option>
                    <option value="Value Added">Value-Added Agricultural Products</option>
                  </optgroup>
                  
                  <option value="other">Other Agricultural Products</option>
                </select>
                {errors.productInterest && (
                  <p className="mt-1 text-sm text-red-600">{errors.productInterest.message}</p>
                )}
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-medium text-gray-700">
                  Your Inquiry
                </label>
                <textarea
                  id="message"
                  rows={4}
                  {...register('message', { required: 'Please provide details about your inquiry' })}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm"
                  placeholder="Tell us about your export needs, quantities, timeline, etc..."
                />
                {errors.message && (
                  <p className="mt-1 text-sm text-red-600">{errors.message.message}</p>
                )}
              </div>

              <div>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                >
                  {isSubmitting ? (
                    <span className="flex items-center">
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Submitting...
                    </span>
                  ) : (
                    'Submit Inquiry'
                  )}
                </button>
              </div>
            </form>
          )}
        </motion.div>
      </div>
    </section>
  );
};

export default ExportInquiryForm; 